import { z } from 'zod';

/**
 * Autopilot's `EmailTriage` workflow (see docs/autopilot-contract.md, "Email triage"). Built in
 * autopilot but NOT deployed yet: ops-desk only talks to it with `TRIAGE_MODE=temporal`.
 *
 * One run per inbound service-mailbox message. Autopilot finds the policy, proposes diary tasks,
 * then waits for an operator's `review` signal; ops-desk reads the proposal from the run's
 * history (activity results), so no worker has to be running to show it.
 *
 * Shapes mirror autopilot's `autopilot_email_triage.dtos` and are loose: fields autopilot adds
 * later pass through.
 */
export const EMAIL_TRIAGE_WORKFLOW = 'EmailTriage';
export const EMAIL_TRIAGE_TASK_QUEUE = 'email-triage';
export const EMAIL_TRIAGE_REVIEW_SIGNAL = 'review';
/** Activity types whose results ops-desk decodes. */
export const EMAIL_TRIAGE_ACTIVITIES = {
  findPolicy: 'find_policy',
  propose: 'propose_diary_tasks',
  prepare: 'prepare_diary_posts',
} as const;
/** The proposal shape ops-desk understands; a newer one is shown as unsupported. */
export const EMAIL_TRIAGE_SCHEMA_VERSION = 1;
/** Autopilot caps the body it reads; ops-desk sends at most this much. */
export const EMAIL_TRIAGE_MAX_BODY_CHARS = 20_000;

/** One run per inbound message. Graph immutable ids are URL-safe base64, so the id is too. */
export function emailTriageWorkflowId(mailbox: string, messageId: string): string {
  return `email-triage:${mailbox.toLowerCase()}:${messageId}`;
}

const person = z.looseObject({ name: z.string(), email: z.string() });

export const emailTriageInputSchema = z.looseObject({
  mailbox: z.string().min(1),
  conversationId: z.string().min(1),
  messageId: z.string().min(1),
  receivedAt: z.iso.datetime({ offset: true }),
  subject: z.string(),
  sender: person,
  /** Plain text: HTML converted, quoted history trimmed, at most EMAIL_TRIAGE_MAX_BODY_CHARS. */
  bodyText: z.string().max(EMAIL_TRIAGE_MAX_BODY_CHARS),
  attachmentNames: z.array(z.string()),
  /** Absent when the email sync started it; the operator for a manual start. */
  requestedBy: z.looseObject({ sub: z.string(), email: z.string() }).optional(),
});
export type EmailTriageInput = z.infer<typeof emailTriageInputSchema>;

export const policyCandidateSchema = z.looseObject({
  number: z.string(),
  /** D01 NEOS Life Insurance, M01 Encompass Protection, S01 Futura Protection. */
  product: z.string(),
  source: z.enum(['subject', 'body', 'attachment']),
  evidence: z.string(),
  /** `null` when it was not looked up. */
  found: z.boolean().nullable(),
});
export type PolicyCandidate = z.infer<typeof policyCandidateSchema>;

const portalUser = z.looseObject({ name: z.string(), uuid: z.string().nullable() });

export const policyContextSchema = z.looseObject({
  policyNumber: z.string(),
  quoteNumber: z.string(),
  productCode: z.string(),
  productName: z.string(),
  customerName: z.string(),
  adviserName: z.string(),
  csm: portalUser,
  underwriter: portalUser,
});
export type PolicyContext = z.infer<typeof policyContextSchema>;

export const policyMatchStatusSchema = z.enum([
  'matched',
  'unverified',
  'ambiguous',
  'not_found',
  'none',
]);
export type PolicyMatchStatus = z.infer<typeof policyMatchStatusSchema>;

export const policyMatchSchema = z.looseObject({
  status: policyMatchStatusSchema,
  method: z.string(),
  policyNumber: z.string().nullable(),
  candidates: z.array(policyCandidateSchema),
  policy: policyContextSchema.nullable(),
});
export type PolicyMatch = z.infer<typeof policyMatchSchema>;

export const diarySuggestionSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['I', 'C']),
  assignee: z.looseObject({
    role: z.enum(['csm', 'underwriter', 'unassigned']),
    name: z.string(),
    uuid: z.string().nullable(),
  }),
  followUpDate: z.iso.date().nullable(),
  notes: z.string(),
  rationale: z.string(),
  confidence: z.number().min(0).max(1),
  /** Every suggestion comes from the model (autopilot has no keyword rules). */
  source: z.enum(['ai']),
});
export type DiarySuggestion = z.infer<typeof diarySuggestionSchema>;

export const tokenUsageSchema = z.looseObject({
  provider: z.enum(['bedrock', 'mock']),
  model: z.string(),
  calls: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  cacheReadTokens: z.number().int(),
  cacheWriteTokens: z.number().int(),
  latencyMs: z.number().int(),
});
export type TokenUsage = z.infer<typeof tokenUsageSchema>;

export const triageProposalSchema = z.looseObject({
  schemaVersion: z.number().int(),
  policy: policyMatchSchema,
  category: z.string(),
  summary: z.string(),
  needsAction: z.boolean(),
  needsReview: z.boolean(),
  suggestions: z.array(diarySuggestionSchema),
  hasSuggestions: z.boolean(),
  /**
   * What the run does next: `review` (waits for an operator), `manual` (no policy number, so
   * nothing was proposed) or `none`. Absent from runs made before autopilot added it.
   */
  route: z.enum(['review', 'manual', 'none']).optional(),
  usage: tokenUsageSchema,
});
export type TriageProposal = z.infer<typeof triageProposalSchema>;

/** An approved suggestion as the operator left it (possibly edited). */
export const reviewedTaskSchema = z.strictObject({
  suggestionId: z.string().max(64).optional(),
  code: z.string().trim().min(1).max(10),
  status: z.enum(['I', 'C']),
  toUserUuid: z.string().max(64).optional(),
  followUpDate: z.iso.date().optional(),
  notes: z.string().trim().max(500),
});
export type ReviewedTask = z.infer<typeof reviewedTaskSchema>;

/** Payload of the `review` signal. */
export const triageReviewSignalSchema = z.object({
  decision: z.enum(['approve', 'dismiss']),
  tasks: z.array(reviewedTaskSchema),
  reason: z.string().optional(),
  reviewedBy: z.object({ sub: z.string(), email: z.string(), name: z.string().optional() }),
  reviewedAt: z.iso.datetime({ offset: true }),
});
export type TriageReviewSignal = z.infer<typeof triageReviewSignalSchema>;

/** Result of `prepare_diary_posts`: what the review led to. */
export const diaryPostsSchema = z.looseObject({
  /** `invalid`: autopilot could not read the review signal (not an operator's dismissal). */
  decision: z.enum(['approve', 'dismiss', 'timed_out', 'invalid']),
  /** False while diary creation from email is switched off in autopilot. */
  createEnabled: z.boolean(),
  unsupported: z.array(z.looseObject({ code: z.string(), reason: z.string() })),
  /** What would be (or was) posted; ops-desk reads the code and status only. */
  planned: z.array(
    z.looseObject({
      outcome: z.looseObject({ diaryTaskCode: z.string(), diaryStatusCode: z.string() }),
    }),
  ),
});
export type DiaryPosts = z.infer<typeof diaryPostsSchema>;

/**
 * Diary task codes triage may propose (autopilot's `catalogue.TRIAGE_DIARY_CODES`). An operator
 * editing a suggestion picks from this list; autopilot rejects any other code.
 */
export const TRIAGE_DIARY_CODES: Readonly<Record<string, string>> = {
  IACH: 'Account address changed',
  IDCH: 'Identity details changed',
  ALTR: 'Alteration request',
  PCDC: 'Periodic credit details changed',
  CBBKD: 'Call Back Bank Details',
  CBRPT: 'Call Back - Retention/Payments Team',
  CBFUP: 'Call Back - Follow-up Enquiry',
  PCAN: 'Policy cancellation',
  BCAN: 'Benefit cancellation',
  SCRPS: 'Send current policy schedule',
  COCS: 'Certificate of currency sent',
  STRE: 'Statement Request',
  IENQ: 'Inforce enquiry',
  ENQY: 'Enquiry',
  CENQ: 'Commission enquiry',
  CLAD: 'Claim admin',
  NTODT: 'Notification of death',
  FACH: 'Servicing Adviser Change',
  RCQR: 'Refund requested',
  INENQ: 'Internal Enquiry',
};
